import { useState } from 'react';
import { MapPin, Users } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { dayKeyParts, MONTH_NAMES, WEEKDAY_NAMES } from '../../lib/calendar/zonedCalendar';
import { formatInZone } from '../../lib/timezone';
import { eventTint, useHiddenTagIds } from './EventTagChip';

/**
 * The "+N more" panel on a busy Month cell — the escape hatch, kept (#679).
 * Labels render in the display zone; the old panel used browser-local time.
 *
 * Props:
 *   dayKey, todayKey, displayZone
 *   events: every event on that day (not only the overflow)
 *   onEventClick(event), onJumpToDay(dayKey)
 *   children: the trigger
 *
 * The content renders in a Radix portal, but React events still bubble up the
 * COMPONENT tree — to the Month cell, whose click creates an event. Every click
 * inside the panel therefore stops at its root (#679 QA: a row and "Open Day
 * view" both opened New event).
 */
export function DayOverflowPopover({ dayKey, todayKey, events, onEventClick, onJumpToDay, displayZone, children }) {
    const p = dayKeyParts(dayKey);
    const [open, setOpen] = useState(false);
    const hiddenTagIds = useHiddenTagIds();
    const label = `${dayKey === todayKey ? 'Today, ' : `${WEEKDAY_NAMES[p.weekday].slice(0, 3)}, `}${p.day} ${MONTH_NAMES[p.month].slice(0, 3)}`;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{children}</PopoverTrigger>
            <PopoverContent
                className="w-80 p-0 rounded-xl border-[#E9E7E2] shadow-[0_12px_28px_rgba(12,10,9,.13)]"
                align="start"
                side="right"
                sideOffset={8}
                data-testid="day-overflow-popover"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3 border-b border-[#EDEBE6]">
                    <h3 className="text-[13px] font-semibold text-[#0C0A09]">{label}</h3>
                    <p className="text-[11.5px] text-[#A29B95]">{events.length} events</p>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-[#F2F0EC]">
                    {events.map(ev => {
                        const tint = eventTint(ev, hiddenTagIds);
                        const minutes = Math.round((new Date(ev.end) - new Date(ev.start)) / 60000);
                        return (
                            <button
                                key={ev.event_id}
                                type="button"
                                onClick={() => { setOpen(false); onEventClick(ev); }}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#FBFAF9] flex items-start gap-3"
                                data-testid={`popover-event-${ev.event_id}`}
                            >
                                <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: tint.ink }} />
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-baseline gap-2">
                                        <span className="text-xs font-semibold text-[#57504B] shrink-0">
                                            {ev.all_day ? 'All day' : formatInZone(ev.start, { zone: displayZone, formatStr: 'h:mm a' })}
                                        </span>
                                        <span className="text-[13px] font-medium text-[#0C0A09] truncate">{ev.title}</span>
                                    </span>
                                    <span className="flex items-center gap-3 text-[11.5px] text-[#A29B95] mt-0.5">
                                        {!ev.all_day && <span>{minutes} min</span>}
                                        {ev.attendees?.length > 0 && (
                                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.attendees.length}</span>
                                        )}
                                        {ev.location && (
                                            <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{ev.location}</span>
                                        )}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
                <div className="px-4 py-2 border-t border-[#EDEBE6]">
                    <button
                        type="button"
                        onClick={() => { setOpen(false); onJumpToDay?.(dayKey); }}
                        className="text-xs font-medium text-[#4D11F6] hover:underline"
                        data-testid="popover-jump-to-day"
                    >
                        Open Day view →
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
